import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, of } from 'rxjs';
import { shareReplay, take, tap } from 'rxjs/operators';
import { LogInSignUpComponent } from '../log-in-sign-up/log-in-sign-up.component';
import { Log } from '../../logger.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class LoginDialogService {
  private dialogRef: MatDialogRef<LogInSignUpComponent> | null = null;
  private loginSubject = new Subject<boolean>();
  private loginResult$: Observable<boolean>;
  private isDialogOpen = false;

  constructor(
    private dialog: MatDialog,
    private authService: AuthService
  ) {
    // Create a shared observable that all subscribers can listen to
    this.loginResult$ = this.loginSubject.asObservable().pipe(shareReplay(1));
  }

  // Opens the login component in a dialog if it's not already open
  openLoginDialog(): Observable<boolean> {
    if (this.authService.isLoggedInValue) {
      return of(false);
    }

    // If dialog is already open, return the existing result observable
    if (this.isDialogOpen) {
      return this.loginResult$;
    }

    // Open a new dialog
    this.isDialogOpen = true;
    this.dialogRef = this.dialog.open(LogInSignUpComponent, {
      width: 'auto',
      minWidth: '400px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      panelClass: 'login-dialog-container',
      backdropClass: 'login-dialog-backdrop',
      disableClose: false, // Allow closing by clicking outside
      data: { isDialog: true, isPalMode: this.authService.palModeValue },
    });

    // Handle dialog close
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        this.isDialogOpen = false;
        this.dialogRef = null;
        // Emit the result to all subscribers
        this.loginSubject.next(!!result);

        // Reset after a short delay to allow all subscribers to receive the result
        setTimeout(() => this.reset(), 100);
      });

    return this.loginResult$;
  }

  // Manually close the dialog if it's open
  closeDialog(result: boolean = false): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    }
  }

  // Reset the service state to handle subsequent authentication requests
  private reset(): void {
    // Create a new subject for future login attempts
    this.loginSubject = new Subject<boolean>();
    this.loginResult$ = this.loginSubject.asObservable().pipe(shareReplay(1));
  }
}
